// The species summary pie, from a pivot of [genus, species] rows: species
// grouped under their genus, with the ones the user hid left out of the
// slices and the percentages.
import { OTHER_COLOR } from "../../../../../utils/chartPalette";
import { OTHER_SPECIES } from "./chartData";

/** "Aedes aegypti" under Aedes is "aegypti"; a non-species result keeps its name. */
export const epithetOf = (genus, label) =>
  label.toLowerCase().startsWith(`${genus.toLowerCase()} `) ? label.slice(genus.length + 1) : label;

/**
 * [{genus, category, species: [{label, epithet, total}]}], in the server's
 * order: genera alphabetically, then Unknown species, Bycatch and the rest.
 */
export const pieGroups = (model) => {
  const groups = [];
  model.rows.forEach((row, r) => {
    const [genus, label] = row.label;
    const category = row.category[1] ?? row.category[0];
    let group = groups[groups.length - 1];
    if (!group || group.genus !== genus) {
      group = { genus, category, species: [] };
      groups.push(group);
    }
    group.species.push({ label, epithet: epithetOf(genus, label), total: model.rowTotals[r] ?? 0 });
  });
  return groups;
};

const sum = (items) => items.reduce((n, i) => n + i.total, 0);

/**
 * The slices for `mode` ("genus" or "species") over what is not hidden.
 * Species mode folds species without a color of their own into one Other
 * species slice. Each slice: {key, label, total, color, share}.
 */
export const pieSlices = (groups, hidden, mode, { colorOf, isOther }) => {
  const shown = groups
    .map((g) => ({ ...g, species: g.species.filter((s) => !hidden.has(s.label)) }))
    .filter((g) => g.species.length);
  const total = sum(shown.flatMap((g) => g.species)) || 1;
  let slices;
  if (mode === "genus") {
    // a genus wears the color of its most-caught species
    slices = shown.map((g) => {
      const top = [...g.species].sort((a, b) => b.total - a.total)[0];
      return { key: g.genus, label: g.genus, total: sum(g.species), color: colorOf(top.label) };
    });
  } else {
    const species = shown.flatMap((g) => g.species.map((s) => ({ ...s, category: g.category })));
    const own = species.filter((s) => s.category !== "species" || !isOther(s.label));
    const folded = species.filter((s) => s.category === "species" && isOther(s.label));
    slices = [
      ...own.filter((s) => s.category === "species").map((s) => ({ key: s.label, label: s.label, total: s.total, color: colorOf(s.label) })),
      ...(folded.length
        ? [{ key: OTHER_SPECIES, label: OTHER_SPECIES, total: sum(folded), color: OTHER_COLOR, members: folded.map((s) => s.label) }]
        : []),
      ...own.filter((s) => s.category !== "species").map((s) => ({ key: s.label, label: s.label, total: s.total, color: colorOf(s.label) })),
    ];
  }
  return { total: sum(shown.flatMap((g) => g.species)), slices: slices.filter((s) => s.total > 0).map((s) => ({ ...s, share: s.total / total })) };
};

export const percent = (share) => {
  const p = share * 100;
  if (p > 0 && p < 0.1) return "<0.1%";
  return `${p.toFixed(p < 10 ? 1 : 0)}%`;
};

/** The legend table as spreadsheet rows: genus, species (epithet), count, percent of what is shown. */
export const pieDataRows = (groups, hidden) => {
  const total = groups.flatMap((g) => g.species).filter((s) => !hidden.has(s.label)).reduce((n, s) => n + s.total, 0) || 1;
  return [
    ["Genus", "Species", "Count", "Percent of total"],
    ...groups.flatMap((g) =>
      g.species
        .filter((s) => !hidden.has(s.label))
        .map((s) => [g.category === "species" ? g.genus : "", g.category === "species" ? s.epithet : s.label, s.total, ((s.total / total) * 100).toFixed(1)])
    ),
  ];
};

// ---- geometry -----------------------------------------------------------

const point = (cx, cy, r, angle) => [cx + r * Math.sin(angle), cy - r * Math.cos(angle)];

/** An annular sector from angle a0 to a1 (radians, clockwise from 12 o'clock). */
export const arcPath = (cx, cy, r0, r1, a0, a1) => {
  if (a1 - a0 >= Math.PI * 2 - 1e-6) a1 = a0 + Math.PI * 2 - 1e-4;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = point(cx, cy, r1, a0);
  const [x1, y1] = point(cx, cy, r1, a1);
  const [x2, y2] = point(cx, cy, r0, a1);
  const [x3, y3] = point(cx, cy, r0, a0);
  return `M${x0},${y0}A${r1},${r1} 0 ${large} 1 ${x1},${y1}L${x2},${y2}A${r0},${r0} 0 ${large} 0 ${x3},${y3}Z`;
};

/**
 * Where each slice's label goes: outside the ring on its own side, pushed
 * apart vertically so no two overlap (`gap` px between baselines), and kept
 * within [top, bottom].
 */
export const layoutLabels = (slices, { cx, cy, r, gap = 16, top = 0, bottom = Infinity }) => {
  let angle = 0;
  const placed = slices.map((s) => {
    const a0 = angle;
    angle += s.share * Math.PI * 2;
    const mid = (a0 + angle) / 2;
    const anchor = point(cx, cy, r + 6, mid);
    const elbow = point(cx, cy, r + 18, mid);
    return { ...s, a0, a1: angle, mid, anchor, elbow, right: Math.sin(mid) >= 0, y: elbow[1] };
  });
  [true, false].forEach((right) => {
    const side = placed.filter((p) => p.right === right).sort((a, b) => a.y - b.y);
    for (let i = 1; i < side.length; i++) side[i].y = Math.max(side[i].y, side[i - 1].y + gap);
    const overflow = side.length ? side[side.length - 1].y - bottom : 0;
    if (overflow > 0) side.forEach((p) => (p.y -= overflow));
    for (let i = side.length - 2; i >= 0; i--) side[i].y = Math.min(side[i].y, side[i + 1].y - gap);
    side.forEach((p) => (p.y = Math.max(p.y, top)));
  });
  placed.forEach((p) => {
    p.textX = cx + (p.right ? 1 : -1) * (r + 30);
  });
  return placed;
};
